import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Building2, User, MapPin, Upload, Check, ArrowRight, ArrowLeft, 
  Loader2, Phone, Mail, ImageIcon, FileText, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const steps = [
  { id: 'a', title: 'Unternehmen', icon: Building2, description: 'Firmendaten' },
  { id: 'b', title: 'Kontakt', icon: MapPin, description: 'Adresse & Telefon' },
  { id: 'c', title: 'Branding', icon: Upload, description: 'Logo & Menü' },
  { id: 'd', title: 'Fertig', icon: Check, description: 'Loslegen!' }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  
  const [formData, setFormData] = useState({
    // Step A
    company_name: '',
    contact_name: '',
    // Step B
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    // Step C
    logo: null,
    menu: null
  });

  // Load initial data
  useEffect(() => {
    const loadTenant = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('access_token');
            navigate('/login');
            return;
          }
          throw new Error('Failed to load status');
        }
        
        const data = await response.json();
        setTenant(data.tenant);
        
        // Set initial form data from tenant
        if (data.tenant) {
          setFormData(prev => ({
            ...prev,
            company_name: data.tenant.company_name || '',
            contact_name: data.tenant.contact_name || '',
            phone: data.tenant.phone || '',
            street: data.tenant.address?.street || '',
            postal_code: data.tenant.address?.postal_code || '',
            city: data.tenant.address?.city || ''
          }));
          
          // Set current step based on progress
          setCurrentStep(Math.min(data.current_step, 3));
          
          // If onboarding is complete, redirect to app
          if (data.is_completed) {
            navigate('/app');
          }
        }
      } catch (error) {
        console.error('Error loading onboarding status:', error);
      }
    };
    
    loadTenant();
  }, [navigate]);

  // Simulate payment webhook after registration
  useEffect(() => {
    const simulatePayment = async () => {
      if (tenant && tenant.subscription_status === 'pending') {
        const token = localStorage.getItem('access_token');
        try {
          await fetch(`${API_URL}/api/webhooks/flotto/simulate`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              event: 'subscription.paid',
              tenant_id: tenant.tenant_id,
              plan: tenant.plan
            })
          });
          
          // Reload tenant data
          const response = await fetch(`${API_URL}/api/onboarding/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          setTenant(data.tenant);
          
          toast.success('Abo aktiviert!', {
            description: `Ihr ${tenant.plan.charAt(0).toUpperCase() + tenant.plan.slice(1)}-Paket ist jetzt aktiv.`
          });
        } catch (error) {
          console.error('Payment simulation error:', error);
        }
      }
    };
    
    if (tenant) {
      simulatePayment();
    }
  }, [tenant?.tenant_id]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const handleStepA = async () => {
    if (!formData.company_name || !formData.contact_name) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/step-a`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          company_name: formData.company_name,
          contact_name: formData.contact_name
        })
      });
      
      if (!response.ok) throw new Error('Failed to save step A');
      
      const data = await response.json();
      setTenant(data.tenant);
      setCurrentStep(1);
      toast.success('Firmendaten gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepB = async () => {
    if (!formData.phone || !formData.street || !formData.postal_code || !formData.city) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/step-b`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          phone: formData.phone,
          street: formData.street,
          postal_code: formData.postal_code,
          city: formData.city,
          country: 'DE'
        })
      });
      
      if (!response.ok) throw new Error('Failed to save step B');
      
      const data = await response.json();
      setTenant(data.tenant);
      setCurrentStep(2);
      toast.success('Kontaktdaten gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepC = async (skip = false) => {
    setIsLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('skip', skip.toString());
      
      if (!skip) {
        if (formData.logo) formDataObj.append('logo', formData.logo);
        if (formData.menu) formDataObj.append('menu', formData.menu);
      }
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/onboarding/step-c`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataObj
      });
      
      if (!response.ok) throw new Error('Failed to save step C');
      
      const data = await response.json();
      setTenant(data.tenant);
      setCurrentStep(3);
      toast.success(skip ? 'Schritt übersprungen' : 'Dateien hochgeladen!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to complete');
      
      toast.success('Onboarding abgeschlossen!');
      
      // Update local storage
      const data = await response.json();
      localStorage.setItem('cashx_tenant', JSON.stringify(data.tenant));
      
      // Redirect to app
      setTimeout(() => navigate('/app'), 1000);
    } catch (error) {
      toast.error('Fehler beim Abschließen');
    } finally {
      setIsLoading(false);
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{ backgroundColor: '#0B1220', color: '#E8ECF2' }}
    >
      <Toaster position="top-center" />
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-[#2F80FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">C</span>
            </div>
            <span className="text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Cash-X
            </span>
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Willkommen! Richten wir Ihr Konto ein.
          </h1>
          {tenant && (
            <p className="text-slate-400">
              {tenant.company_name} • {tenant.plan.charAt(0).toUpperCase() + tenant.plan.slice(1)}-Paket
            </p>
          )}
        </div>

        {/* Progress */}
        <div className="mb-8">
          <Progress value={progress} className="h-2 bg-slate-800" />
          <div className="flex justify-between mt-4">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`flex flex-col items-center ${
                  index <= currentStep ? 'text-white' : 'text-slate-500'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                  index < currentStep 
                    ? 'bg-emerald-500' 
                    : index === currentStep 
                      ? 'bg-[#2F80FF]' 
                      : 'bg-slate-700'
                }`}>
                  {index < currentStep ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </div>
                <span className="text-xs hidden sm:block">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Card 
              className="p-6"
              style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
            >
              {/* Step A: Company */}
              {currentStep === 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#2F80FF]/20 rounded-lg flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-[#2F80FF]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">Schritt 1: Unternehmen</h2>
                      <p className="text-slate-400 text-sm">Grundlegende Firmendaten</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white">Firmenname *</Label>
                    <Input
                      data-testid="onboarding-company-name"
                      placeholder="z.B. Pizzeria Milano"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Ansprechpartner *</Label>
                    <Input
                      data-testid="onboarding-contact-name"
                      placeholder="Vor- und Nachname"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    />
                  </div>
                  
                  <Button
                    data-testid="onboarding-step-a-next"
                    onClick={handleStepA}
                    disabled={isLoading}
                    className="w-full bg-[#2F80FF] hover:bg-[#4A92FF] text-white h-11 mt-4"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <>Weiter <ArrowRight className="h-4 w-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              )}

              {/* Step B: Contact */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#2F80FF]/20 rounded-lg flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-[#2F80FF]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">Schritt 2: Kontakt & Adresse</h2>
                      <p className="text-slate-400 text-sm">Für Rechnungen und Support</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white">Telefon *</Label>
                    <Input
                      data-testid="onboarding-phone"
                      placeholder="+49 30 12345678"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Straße & Hausnummer *</Label>
                    <Input
                      data-testid="onboarding-street"
                      placeholder="Musterstraße 123"
                      value={formData.street}
                      onChange={(e) => setFormData({...formData, street: e.target.value})}
                      className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white">PLZ *</Label>
                      <Input
                        data-testid="onboarding-postal"
                        placeholder="10115"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Stadt *</Label>
                      <Input
                        data-testid="onboarding-city"
                        placeholder="Berlin"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(0)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
                    </Button>
                    <Button
                      data-testid="onboarding-step-b-next"
                      onClick={handleStepB}
                      disabled={isLoading}
                      className="flex-1 bg-[#2F80FF] hover:bg-[#4A92FF] text-white h-11"
                    >
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        <>Weiter <ArrowRight className="h-4 w-4 ml-2" /></>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step C: Branding */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#2F80FF]/20 rounded-lg flex items-center justify-center">
                      <Upload className="h-6 w-6 text-[#2F80FF]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">Schritt 3: Branding (optional)</h2>
                      <p className="text-slate-400 text-sm">Logo und Speisekarte hochladen</p>
                    </div>
                  </div>
                  
                  {/* Logo Upload */}
                  <div>
                    <Label className="text-white">Logo</Label>
                    <div 
                      className="mt-1 border-2 border-dashed border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-[#2F80FF]/50 transition-colors"
                      onClick={() => document.getElementById('logo-input').click()}
                    >
                      <input
                        id="logo-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setFormData({...formData, logo: e.target.files[0]})}
                      />
                      {formData.logo ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-400">
                          <Check className="h-5 w-5" />
                          {formData.logo.name}
                        </div>
                      ) : (
                        <div className="text-slate-400">
                          <ImageIcon className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Klicken zum Hochladen (JPG, PNG, WebP)</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Menu Upload */}
                  <div>
                    <Label className="text-white">Speisekarte</Label>
                    <div 
                      className="mt-1 border-2 border-dashed border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-[#2F80FF]/50 transition-colors"
                      onClick={() => document.getElementById('menu-input').click()}
                    >
                      <input
                        id="menu-input"
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => setFormData({...formData, menu: e.target.files[0]})}
                      />
                      {formData.menu ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-400">
                          <Check className="h-5 w-5" />
                          {formData.menu.name}
                        </div>
                      ) : (
                        <div className="text-slate-400">
                          <FileText className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Klicken zum Hochladen (PDF, JPG, PNG)</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleStepC(true)}
                      disabled={isLoading}
                      className="text-slate-400 hover:text-white"
                    >
                      Überspringen
                    </Button>
                    <Button
                      data-testid="onboarding-step-c-next"
                      onClick={() => handleStepC(false)}
                      disabled={isLoading}
                      className="flex-1 bg-[#2F80FF] hover:bg-[#4A92FF] text-white h-11"
                    >
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        <>Weiter <ArrowRight className="h-4 w-4 ml-2" /></>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step D: Complete */}
              {currentStep === 3 && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="h-10 w-10 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Alles bereit!
                  </h2>
                  <p className="text-slate-400 mb-6">
                    Ihr Cash-X Konto ist vollständig eingerichtet. 
                    Starten Sie jetzt mit Ihrem TSE-konformen Kassensystem!
                  </p>
                  
                  {tenant && (
                    <div className="bg-[#0E1628] rounded-lg p-4 mb-6 text-left">
                      <h3 className="font-semibold mb-2">Zusammenfassung:</h3>
                      <ul className="text-sm text-slate-400 space-y-1">
                        <li>• Firma: <span className="text-white">{tenant.company_name}</span></li>
                        <li>• Paket: <span className="text-white">{tenant.plan?.charAt(0).toUpperCase() + tenant.plan?.slice(1)}</span></li>
                        <li>• Status: <span className="text-emerald-400">{tenant.subscription_status === 'active' ? 'Aktiv' : 'Pending'}</span></li>
                      </ul>
                    </div>
                  )}
                  
                  <Button
                    data-testid="onboarding-complete"
                    onClick={handleComplete}
                    disabled={isLoading}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-lg font-medium"
                  >
                    {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                      <>Cash-X starten <ArrowRight className="h-5 w-5 ml-2" /></>
                    )}
                  </Button>
                </div>
              )}
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
